import { useState, useEffect } from "react";

/**
 * ConfirmationPopup Component
 * Displays a modal popup with YES, NO, and Cancel buttons
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether popup is open
 * @param {Function} props.onResult - Callback function that receives the result ('yes', 'no', or 'cancel')
 * @param {string} props.title - Title text for the popup
 * @param {string} props.message - Message text for the popup
 */
export default function ConfirmationPopup({
  isOpen = false,
  onResult,
  title = "Confirmation",
  message = "Please confirm your choice",
}) {
  const [isVisible, setIsVisible] = useState(false);
  

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      // Prevent body scroll when popup is open
      document.body.style.overflow = "hidden";
    } else {
      setIsVisible(false);
      document.body.style.overflow = "unset";
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  const handleButtonClick = (result) => {
    setIsVisible(false);
    document.body.style.overflow = "unset";
    if (onResult && typeof onResult === "function") {
      onResult(result);
    }
  };

  const handleBackdropClick = (e) => {
    // Close only if clicking the backdrop, not the content
    if (e.target === e.currentTarget) {
      handleButtonClick("cancel");
    }
  };

  if (!isOpen || !isVisible) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 p-4"
      onClick={handleBackdropClick}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 animate-in fade-in zoom-in duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="mb-4">
          <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
        </div>

        {/* Message */}
        <div className="mb-6">
          <p className="text-gray-700">{message}</p>
        </div>

        {/* Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 justify-end">
          <button
            onClick={() => handleButtonClick("yes")}
            className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors font-medium"
          >
            YES
          </button>
          <button
            onClick={() => handleButtonClick("no")}
            className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors font-medium"
          >
            NO
          </button>
          <button
            onClick={() => handleButtonClick("cancel")}
            className="px-6 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors font-medium"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

